import _ from 'lodash';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getState } from 'src/store';
import { getWorkspaceRouteModule } from 'src/workspace/selectors';
import { getAppRootElement, isDev } from 'src/workspace/utils';
import { clearStore, setIsLoading, setSummariseData } from '../slice';
import {
  INPUT_LIKE_TAGS,
  INTERACTIVE_ROLES,
  INTERESTING_TAGS,
} from './constants';

const baseURL = isDev()
  ? 'http://localhost:3000'
  : 'https://frontend.getvymo.com';

const isUsefulElement = (el: Element) => {
  const tag = el.tagName;
  const role = el.getAttribute('role');
  if (!INTERESTING_TAGS.includes(tag)) return false;
  if (INTERACTIVE_ROLES.includes(role || '')) return true;

  const text = el.textContent?.trim() || '';
  const hasUsefulText = text.length > 0 && !/^[\s|.,;:!?-]*$/.test(text);

  const isInputLike = INPUT_LIKE_TAGS.includes(tag);

  return hasUsefulText || isInputLike;
};

const isMeaningfulDiv = (div: HTMLElement) => {
  // skip invisible divs
  if (!(div.offsetWidth || div.offsetHeight || div.getClientRects().length))
    return false;

  // keep if it has meaningful text
  const text = div.textContent?.trim() || '';
  if (text.length > 0 && !/^[\s|.,;:!?-]*$/.test(text)) return true;

  // keep if contains any meaningful non-div child
  const childTags = Array.from(div.children).map((c) => c.tagName);
  return childTags.some((t) => t !== 'DIV' && t !== 'SPAN');
};

export const getCurrentUIContext = () => {
  const root = getAppRootElement();
  if (!root) return { nodes: [], userAgent: navigator.userAgent };

  const allElements = Array.from(root.querySelectorAll('*'));
  const nodes = allElements
    .filter((el) => {
      if (!isUsefulElement(el)) return false;
      if (el.tagName === 'DIV') return isMeaningfulDiv(el as HTMLElement);
      return true;
    })
    .map((element) => ({
      tag: element.tagName,
      id: element.id,
      class: element.className,
      text: element.textContent?.trim().slice(0, 80), // truncate long text
      ariaLabel: element.getAttribute('aria-label'),
      role: element.getAttribute('role'),
      name: element.getAttribute('name'),
      type: element.getAttribute('type'),
      value:
        element instanceof HTMLInputElement ||
        element instanceof HTMLSelectElement ||
        element instanceof HTMLTextAreaElement
          ? element.value
          : undefined,
      visible: !!(
        (element as HTMLElement).offsetWidth ||
        (element as HTMLElement).offsetHeight ||
        element.getClientRects().length
      ),
    }));

  return { nodes, userAgent: navigator.userAgent };
};

export const summariseUI = createAsyncThunk(
  'summariseUI',
  async (a: any, { dispatch }) => {
    try {
      dispatch(setIsLoading(true));
      const routeModule = getWorkspaceRouteModule(getState());
      const routeModuleStoreData =
        _.get(getState(), routeModule?.agentic?.slicePath ?? '') ?? {};
      const uiContext = getCurrentUIContext();
      const response = await axios.post(
        `${baseURL}/frontend/api/agentic/summarise`,
        {
          data: routeModuleStoreData,
          ui: uiContext,
        },
        {
          headers: {
            'x-frontendboard-token': process.env.X_FRONTEND_TOKEN,
          },
        },
      );
      //   @ts-ignore
      dispatch(setSummariseData(response.data));
      dispatch(setIsLoading(false));
    } catch (error) {
      dispatch(setIsLoading(false));
    }
  },
);

const onActivate = async () => {};

const onDeactivate = ({ dispatch }) => {
  dispatch(clearStore());
};

export default { onActivate, onDeactivate };
